/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IComponentStyles } from '@fluentui/foundation-legacy';
import { DefaultButton, IButtonProps, IButtonStyles } from '@fluentui/react/lib/Button';
import { Icon, IIconStyleProps, IIconStyles } from '@fluentui/react/lib/Icon';
import { IStackProps, IStackSlots, Stack, StackItem } from '@fluentui/react/lib/Stack';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisStepper } from 'src/core/components/luisStepper/LuisStepper';
import { LuisStepperSeparator } from 'src/core/components/luisStepper/LuisStepperSeparator';
import { LuisText } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme } from 'src/core/themes/luisStylist';

export type LuisStepperStepProps = LuisStepperStepType & {
    index: number;
    depth: number;
    stepsCount: number;
    horizontal?: boolean;
    onStepClick: (key: string) => void;
};

export type LuisStepperStepType = {
    id: string;
    title?: string;
    disabled?: boolean;
    optional?: boolean;
    isNumeric?: boolean;
    children?: LuisStepperStepType[];
    stepButtonProps?: IButtonProps;
    state: 'active' | 'completed' | 'none';
    onRenderStepLabel?: () => React.ReactNode;
};

const messages = defineMessages({
    optional: {
        id: 'portal.luisStepper.optional',
        defaultMessage: 'optional'
    }
});

const stepperTheme = getScopedTheme('LuisStepper');
const buttonStyler = classNamesFunction<IButtonProps, IButtonStyles>();
const stackStyler = classNamesFunction<IStackProps, IComponentStyles<IStackSlots>>();

const expandingContainerWidthStyles = stackStyler({ root: { width: '100%' } });
const separatorContainerStyles = stackStyler({ root: { width: '100%', marginBottom: '4px' } });
const verticalStepButtonStyles = buttonStyler({ root: { maxHeight: '40px' }, flexContainer: { alignItems: 'stretch' } });
const numericIconContainer = stackStyler({
    root: { border: `1px solid ${stepperTheme.defaultColor}`, borderRadius: '50%', width: '40px', height: '40px' }
});

const noneIconStyles = classNamesFunction<IIconStyleProps, IIconStyles>()({ root: { color: stepperTheme.noneStepIconColor } });
const stepIconStyles = classNamesFunction<IIconStyleProps, IIconStyles>()({
    root: { fontWeight: 700, color: stepperTheme.defaultColor, paddingTop: '3px' }
});
const stepButtonStyles = buttonStyler({
    rootHovered: { backgroundColor: 'transparent' },
    rootPressed: { backgroundColor: 'transparent' },
    rootDisabled: { backgroundColor: 'transparent', color: stepperTheme.disabledStepColor },
    root: { padding: 0, border: 'none', maxHeight: '100%', height: 'auto', minWidth: 'auto', maxWidth: '100%' }
});

/**
 * Returns the corresponding icon name to the given step status.
 */
const getIconName = (status: LuisStepperStepProps['state'], depth: number): string => {
    if (depth > 0) {
        switch (status) {
            case 'active':
            case 'completed':
            case 'none':
            default:
                return 'LocationDot';
        }
    }

    switch (status) {
        case 'active':
            return 'CircleFill';
        case 'completed':
            return 'CompletedSolid';
        case 'none':
        default:
            return 'CircleRing';
    }
};

export const LuisStepperStep = (props: LuisStepperStepProps) => {
    const {
        id,
        index,
        title,
        state,
        depth,
        disabled,
        optional,
        horizontal,
        stepsCount,
        onStepClick,
        children = [],
        stepButtonProps,
        isNumeric = false,
        onRenderStepLabel
    } = props;
    const { localize } = useLocalization();

    const hasInnerStepper = children.length > 0 && state === 'active';
    const isLastStep = index === stepsCount - 1;

    const renderInternalSeparator = (hide: boolean) =>
        !hide && (
            <Stack grow verticalAlign="stretch">
                <LuisStepperSeparator
                    isNumeric={isNumeric}
                    horizontal={horizontal}
                    isContainerFullWidth={false}
                    isCompleted={state === 'completed'}
                />
            </Stack>
        );

    return (
        <Stack
            grow
            verticalAlign="start"
            horizontalAlign="start"
            horizontal={horizontal}
            data-automation-id={id}
            tokens={{ childrenGap: !horizontal && !isNumeric && 4 }}>
            <DefaultButton
                disabled={disabled}
                onClick={() => onStepClick && onStepClick(id)}
                {...stepButtonProps}
                styles={mergeStyleSets(
                    stepButtonStyles,
                    !horizontal && verticalStepButtonStyles,
                    stepButtonProps && stepButtonProps.styles
                )}>
                <Stack
                    horizontal={!horizontal}
                    tokens={{ childrenGap: 12 }}
                    styles={expandingContainerWidthStyles}
                    verticalAlign={horizontal ? 'center' : 'stretch'}>
                    <Stack
                        horizontal={horizontal}
                        verticalAlign="stretch"
                        verticalFill={!horizontal}
                        tokens={{ childrenGap: 4 }}
                        styles={horizontal && expandingContainerWidthStyles}>
                        {renderInternalSeparator(!horizontal || index === 0)}
                        {isNumeric ? (
                            <Stack verticalAlign="center" horizontalAlign="center" styles={numericIconContainer}>
                                <LuisText color={stepperTheme.defaultColor} variant="large" lineHeight="24px">
                                    {index + 1}
                                </LuisText>
                            </Stack>
                        ) : (
                            <Icon
                                iconName={getIconName(state, depth)}
                                styles={mergeStyleSets(stepIconStyles, state === 'none' && noneIconStyles)}
                            />
                        )}
                        {horizontal && renderInternalSeparator(isLastStep)}
                    </Stack>

                    {onRenderStepLabel ? (
                        onRenderStepLabel()
                    ) : (
                        <Stack
                            data-automation-id="stepTitle"
                            horizontalAlign={horizontal ? 'center' : 'start'}
                            styles={{ root: { display: 'inline-block', overflow: horizontal ? 'unset' : 'hidden' } }}>
                            <LuisText
                                title={title}
                                fontWeight={state === 'active' && 600}
                                variant={horizontal ? 'small' : 'medium'}
                                styles={!horizontal && { root: { whiteSpace: 'nowrap' } }}>
                                {title}
                            </LuisText>

                            {optional && <LuisText styles={{ root: { fontStyle: 'italic' } }}>({localize(messages.optional)})</LuisText>}
                        </Stack>
                    )}
                </Stack>
            </DefaultButton>

            {!isLastStep && (
                <StackItem styles={separatorContainerStyles}>
                    <LuisStepperSeparator isNumeric={isNumeric} horizontal={horizontal} isCompleted={state === 'completed'} />
                </StackItem>
            )}

            {hasInnerStepper && <LuisStepper steps={children} depth={depth + 1} horizontal={horizontal} onStepClick={onStepClick} />}

            {hasInnerStepper && !isLastStep && (
                <StackItem styles={separatorContainerStyles}>
                    <LuisStepperSeparator
                        isNumeric={isNumeric}
                        horizontal={horizontal}
                        isCompleted={children.every(child => child.state === 'completed')}
                    />
                </StackItem>
            )}
        </Stack>
    );
};
